import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  FileButton,
  Group,
  Image,
  Loader,
  NumberInput,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  Textarea,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { COUNTRIES } from '@wwaave/shared/vocab';
import { useEffect, useRef, useState } from 'react';

import { AddressLookup } from '../../../components/AddressLookup';
import { AppShell } from '../../../components/app-shell/AppShell';
import { EmptyState, MotionSection, PageHero } from '../../../components/ui';
import { VENUE_FORM_DEFAULTS } from '../../../components/venue/defaults';
import type { VenueFormValues } from '../../../components/venue/types';
import {
  VenueAmenitiesFields,
  VenueExtrasFields,
  VenueStipendFields,
  VenueTypeAndVibeFields,
} from '../../../components/venue/VenueFormFields';
import { useApiForm } from '../../../hooks/useApiForm';
import { fetchOk } from '../../../lib/api';
import { csrfHeaders } from '../../../lib/auth-context';
import {
  fetchHostVerifications,
  summariseHostVerificationState,
} from '../../../lib/host-verification';
import { track } from '../../../lib/telemetry';
import { showToast } from '../../../lib/toast';
import { uploadImage } from '../../../lib/upload';
import { VENUE_STATUS_LABEL, type VenueStatus } from '../../../lib/venue-status';

/**
 * /host/venues/$id/edit — full venue edit form + photo gallery + calendar.
 *
 * CRITICAL: every multi-select pre-populates from the saved venue. The form
 * is initialised with empty defaults and `setValues()` runs once the GET
 * resolves. Without this, saving the form would clear the m2m join tables —
 * the explicit fix for the audit's known Bubble bug.
 *
 * Photo and calendar editing are isolated panels — each with its own
 * mutation — so saving the main form doesn't disturb them.
 */

type CalendarEntry = {
  id: string;
  entry_kind: 'block' | 'open_window';
  start_date: string;
  end_date: string;
  recurrence_rule: string | null;
  notes: string | null;
};

type Photo = {
  id: string;
  url: string;
  alt_text: string;
  caption: string | null;
  position: number;
  is_hero: boolean;
  width_px: number;
  height_px: number;
};

type VenueDetail = {
  ok: true;
  venue: {
    id: string;
    name: string;
    slug: string;
    summary: string | null;
    description: string;
    capacity_min: number;
    capacity_max: number;
    address_line: string;
    city: string;
    region: string | null;
    country: string;
    postcode: string | null;
    lat: number;
    lng: number;
    address_visible_to_public: boolean;
    timezone: string;
    usual_event_times: string | null;
    house_rules: string | null;
    stipend_offered: boolean;
    stipend_description: string | null;
    travel_contribution_default?: boolean;
    travel_contribution_description?: string | null;
    equipment_default?: boolean;
    equipment_description?: string | null;
    promotion_default?: boolean;
    promotion_description?: string | null;
    recording_default?: boolean;
    recording_description?: string | null;
    status: VenueStatus;
    verified_at: number | null;
  };
  photos: Photo[];
  art_forms_accepted: string[];
  venue_types: string[];
  vibe_tags: string[];
  sound_tech: string[];
  sleeping_arrangements: string[];
  food_offered: string[];
  drink_offered: string[];
  facilities: string[];
  calendar_entries: CalendarEntry[];
};

type ApiError = { ok: false; error: { code: string; message: string } };

type FormValues = VenueFormValues;

async function fetchVenue(id: string): Promise<VenueDetail | ApiError> {
  const r = await fetch(`/api/venues/me/venues/${encodeURIComponent(id)}`, {
    credentials: 'include',
  });
  return (await r.json()) as VenueDetail | ApiError;
}

async function transitionStatus(id: string, to: string): Promise<unknown> {
  // fetchOk: the worker answers refusals as text/plain (HTTPException), so a
  // bare r.json() turned e.g. "Not ready: add a hero photo" into a JSON parse error.
  const r = await fetchOk(`/api/venues/me/venues/${encodeURIComponent(id)}/status`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...csrfHeaders() },
    body: JSON.stringify({ to }),
  });
  return r.json();
}

async function uploadPhoto(
  id: string,
  file: File,
  altText: string,
  isHero: boolean,
): Promise<unknown> {
  // Raw-body upload with metadata in the query (lib/upload.ts): a multipart
  // photo is blocked by the zone's firewall before it reaches us.
  const query: Record<string, string> = { alt_text: altText };
  if (isHero) query.is_hero = 'true';
  const r = await uploadImage(`/api/venues/me/venues/${encodeURIComponent(id)}/photos`, file, query);
  return r.json();
}

async function deletePhoto(venueId: string, photoId: string): Promise<unknown> {
  const r = await fetchOk(
    `/api/venues/me/venues/${encodeURIComponent(venueId)}/photos/${encodeURIComponent(photoId)}`,
    { method: 'DELETE', credentials: 'include', headers: csrfHeaders() },
  );
  return r.json();
}

async function reorderPhotos(venueId: string, ordered: string[]): Promise<unknown> {
  const r = await fetchOk(
    `/api/venues/me/venues/${encodeURIComponent(venueId)}/photos/reorder`,
    {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...csrfHeaders() },
      body: JSON.stringify({ ordered_image_ids: ordered }),
    },
  );
  return r.json();
}

async function saveCalendar(
  venueId: string,
  entries: CalendarEntry[],
): Promise<unknown> {
  const r = await fetchOk(
    `/api/venues/me/venues/${encodeURIComponent(venueId)}/calendar`,
    {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...csrfHeaders() },
      body: JSON.stringify({
        entries: entries.map((e) => ({
          entry_kind: e.entry_kind,
          start_date: e.start_date,
          end_date: e.end_date,
          ...(e.recurrence_rule !== null && { recurrence_rule: e.recurrence_rule }),
          ...(e.notes !== null && { notes: e.notes }),
        })),
      }),
    },
  );
  return r.json();
}

function VenueEdit() {
  const navigate = useNavigate();
  const { id } = Route.useParams();
  const qc = useQueryClient();
  const venueQ = useQuery({ queryKey: ['venue', id], queryFn: () => fetchVenue(id) });
  // CFR-011: surface the host's identity-verification gate. Self-publish
  // (draft → pending_review) requires the host to be approved; the backend
  // rejects with a generic 422 otherwise. Fetching here lets us show a clear
  // callout + disable the submit button.
  const verificationQ = useQuery({
    queryKey: ['verifications', 'me'],
    queryFn: fetchHostVerifications,
  });

  const { form, submit, submitting: saving } = useApiForm<FormValues>({
    endpoint: `/api/venues/me/venues/${encodeURIComponent(id)}`,
    method: 'PATCH',
    initialValues: VENUE_FORM_DEFAULTS,
    formOptions: {
      validate: {
        name: (v) => (v.length < 2 ? 'Add a venue name.' : null),
        description: (v) =>
          v.length < 20 ? 'Write at least a sentence or two.' : null,
        venue_types: (v) => (v.length === 0 ? 'Pick at least one venue type.' : null),
        art_forms_accepted: (v) =>
          v.length === 0 ? 'Pick at least one art form you welcome.' : null,
        // CFR-010: 0,0 is the silent default coming over from a partially
        // geocoded venue. Refuse it on the form so the host fixes the address
        // before save.
        lat: (v, all) =>
          typeof v === 'number' && v === 0 && all.lng === 0
            ? 'Set a real lat/lng. The platform uses geocoding to place your venue on the map.'
            : null,
        lng: (v, all) =>
          typeof v === 'number' && v === 0 && all.lat === 0
            ? 'Set a real lat/lng. The platform uses geocoding to place your venue on the map.'
            : null,
        // CFR-004: when stipend is offered, description must be substantive.
        // Hosts who are plain about money convert better — a vague "stipend
        // available" line forces the artist to message just to learn whether it
        // covers fuel.
        stipend_description: (v, all) => {
          if (!all.stipend_offered) return null;
          if (v.trim().length < 10) {
            return 'Tell artists what the stipend covers. At least a sentence.';
          }
          return null;
        },
      },
    },
    transform: (values) => toPatchPayload(values as FormValues),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['venue', id] });
      showToast({ message: 'Saved.', variant: 'success' });
    },
    mapError: (err) => {
      showToast({ message: err.message, variant: 'error' });
      return null;
    },
  });

  // Pre-populate everything once the GET resolves.
  useEffect(() => {
    if (!venueQ.data || !venueQ.data.ok) return;
    const d = venueQ.data;
    form.setValues({
      name: d.venue.name,
      summary: d.venue.summary ?? '',
      description: d.venue.description,
      venue_types: d.venue_types,
      art_forms_accepted: d.art_forms_accepted,
      vibe_tags: d.vibe_tags,
      capacity_min: d.venue.capacity_min,
      capacity_max: d.venue.capacity_max,
      address_line: d.venue.address_line,
      city: d.venue.city,
      region: d.venue.region ?? '',
      country: d.venue.country,
      postcode: d.venue.postcode ?? '',
      lat: d.venue.lat,
      lng: d.venue.lng,
      address_visible_to_public: d.venue.address_visible_to_public,
      timezone: d.venue.timezone,
      usual_event_times: d.venue.usual_event_times ?? '',
      sound_tech: d.sound_tech,
      sleeping_arrangements: d.sleeping_arrangements,
      food_offered: d.food_offered,
      drink_offered: d.drink_offered,
      facilities: d.facilities,
      house_rules: d.venue.house_rules ?? '',
      stipend_offered: d.venue.stipend_offered,
      stipend_description: d.venue.stipend_description ?? '',
      travel_contribution_default: d.venue.travel_contribution_default ?? false,
      travel_contribution_description: d.venue.travel_contribution_description ?? '',
      equipment_default: d.venue.equipment_default ?? false,
      equipment_description: d.venue.equipment_description ?? '',
      promotion_default: d.venue.promotion_default ?? false,
      promotion_description: d.venue.promotion_description ?? '',
      recording_default: d.venue.recording_default ?? false,
      recording_description: d.venue.recording_description ?? '',
    });
    form.resetDirty();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venueQ.data]);

  const transitionM = useMutation({
    mutationFn: (to: string) => transitionStatus(id, to),
    onSuccess: async (_data, to) => {
      await qc.invalidateQueries({ queryKey: ['venue', id] });
      showToast({ message: resolveTransitionToast(to), variant: 'success' });
    },
    onError: (err: Error) => showToast({ message: err.message, variant: 'error' }),
  });

  if (venueQ.isPending) {
    return (
      <AppShell tone="form">
        <Loader />
      </AppShell>
    );
  }
  if (!venueQ.data || !venueQ.data.ok) {
    return (
      <AppShell tone="form">
        <EmptyState
          title="Couldn't load this venue."
          body={
            venueQ.data && !venueQ.data.ok
              ? venueQ.data.error.message
              : 'Try again in a moment.'
          }
          action={
            <Button component={Link} to="/host/venues">
              Back to venues
            </Button>
          }
        />
      </AppShell>
    );
  }

  const detail = venueQ.data;
  const venue = detail.venue;

  // CFR-011: host identity verification gates self-publish. The backend's
  // checkLivenessPreconditions in /me/venues/:id/status enforces this; the
  // SPA mirrors it so the host knows what's blocking them before they tap.
  const hostVerificationState =
    verificationQ.data && verificationQ.data.ok
      ? summariseHostVerificationState(verificationQ.data.verifications)
      : 'none';
  const verificationApproved = hostVerificationState === 'approved';
  const verificationCheckLoaded = verificationQ.isSuccess;
  const verificationCopy = ((): { title: string; body: string } => {
    switch (hostVerificationState) {
      case 'pending':
        return {
          title: 'Verification in review',
          body:
            'Your identity check is with our team. Most decisions land within 72 hours. Publishing unlocks once it\'s approved.',
        };
      case 'rejected':
        return {
          title: 'Verification needs another look',
          body:
            'Your last submission was rejected. Re-submit identity evidence and publishing unlocks once it\'s approved.',
        };
      case 'revoked':
        return {
          title: 'Verification was revoked',
          body:
            'Your identity verification was revoked. Re-submit to restore publishing.',
        };
      default:
        return {
          title: 'Identity verification unlocks publishing',
          body:
            'Submit a government ID and a photo of you holding it. Most reviews land within 72 hours.',
        };
    }
  })();
  const showVerificationGate = venue.status === 'draft' && !verificationApproved;
  const submitTooltipLabel = !verificationCheckLoaded
    ? 'Checking your verification status…'
    : hostVerificationState === 'pending'
      ? 'Publishing unlocks once your identity verification is approved.'
      : hostVerificationState === 'rejected'
        ? 'Your last identity verification was rejected. Re-submit and try again.'
        : hostVerificationState === 'revoked'
          ? 'Your identity verification was revoked. Re-submit to restore publishing.'
          : 'Verify your identity first. Publishing unlocks once it\'s approved.';

  return (
    <AppShell tone="form">
      <Stack gap="lg">
        <MotionSection>
          <PageHero
            eyebrow="Edit venue"
            title={venue.name}
            lede={`${venue.city}, ${venue.country}`}
            action={
              <Group gap="xs">
                <Link
                  to="/host/venues/$id/preview"
                  params={{ id }}
                  style={{ textDecoration: 'none' }}
                >
                  <Button variant="subtle" component="span" radius="md">
                    Preview
                  </Button>
                </Link>
                {venue.status === 'draft' &&
                  (verificationApproved ? (
                    <Button
                      onClick={() => transitionM.mutate('pending_review')}
                      loading={transitionM.isPending}
                      variant="gradient"
                      gradient={{ from: 'brand.6', to: 'sun.5', deg: 35 }}
                      radius="md"
                    >
                      Submit for review
                    </Button>
                  ) : (
                    <Tooltip label={submitTooltipLabel} withArrow position="bottom">
                      {/* span wrapper so the tooltip still fires while the button is disabled */}
                      <span>
                        <Button
                          disabled
                          variant="gradient"
                          gradient={{ from: 'brand.6', to: 'sun.5', deg: 35 }}
                          radius="md"
                          aria-describedby="publish-verification-gate"
                        >
                          Submit for review
                        </Button>
                      </span>
                    </Tooltip>
                  ))}
                {venue.status === 'live' && (
                  <Button
                    variant="light"
                    onClick={() => transitionM.mutate('paused')}
                    loading={transitionM.isPending}
                    radius="md"
                  >
                    Pause venue
                  </Button>
                )}
                {venue.status === 'paused' && (
                  <Button
                    onClick={() => transitionM.mutate('draft')}
                    loading={transitionM.isPending}
                    radius="md"
                  >
                    Move back to draft
                  </Button>
                )}
              </Group>
            }
          />
          <Group gap="xs" mt="xs">
            {/* AUD-012: humanised status — the raw enum ('pending_review')
                was leaking straight into this badge. */}
            <Badge color={VENUE_STATUS_LABEL[venue.status].color} variant="light">
              {VENUE_STATUS_LABEL[venue.status].label}
            </Badge>
            {venue.verified_at !== null && (
              <Badge color="brand" variant="light">
                Verified
              </Badge>
            )}
          </Group>
          {venue.status === 'pending_review' && (
            // AUD-012: the only explanatory alert used to be gated to
            // status==='draft' — it vanished the moment a host submitted,
            // leaving "in review" unexplained. This mirrors that Alert's
            // shape for the review window.
            <Alert
              mt="md"
              color="tide"
              variant="light"
              radius="md"
              title="Review in progress"
            >
              <Stack gap="sm">
                <Text size="sm">
                  Your venue is with our team. Most decisions land within 72 hours — we'll
                  notify you either way.
                </Text>
                <Group>
                  <Link
                    to="/host/venues/$id/preview"
                    params={{ id }}
                    style={{ textDecoration: 'none' }}
                  >
                    <Button component="span" size="sm" variant="light" radius="md">
                      Preview your venue
                    </Button>
                  </Link>
                </Group>
              </Stack>
            </Alert>
          )}
          {showVerificationGate && (
            <Alert
              id="publish-verification-gate"
              mt="md"
              color="brand"
              variant="light"
              radius="md"
              title={verificationCopy.title}
            >
              <Stack gap="sm">
                <Text size="sm">{verificationCopy.body}</Text>
                <Group>
                  <Button
                    component={Link}
                    to="/verification"
                    size="sm"
                    variant="gradient"
                    gradient={{ from: 'brand.6', to: 'sun.5', deg: 35 }}
                    radius="md"
                  >
                    {hostVerificationState === 'pending'
                      ? 'View status'
                      : hostVerificationState === 'rejected' ||
                          hostVerificationState === 'revoked'
                        ? 'Re-submit verification'
                        : 'Verify now'}
                  </Button>
                </Group>
              </Stack>
            </Alert>
          )}
        </MotionSection>

        <form onSubmit={submit}>
          <Stack gap="lg">
            <Paper p="md" withBorder radius="md">
              <Stack gap="md">
                <Title order={3}>Basics</Title>
                <TextInput label="Venue name" required {...form.getInputProps('name')} />
                <TextInput label="One-line summary" {...form.getInputProps('summary')} />
                <Textarea
                  label="Description"
                  required
                  autosize
                  minRows={5}
                  maxRows={20}
                  {...form.getInputProps('description')}
                />
              </Stack>
            </Paper>

            <VenueTypeAndVibeFields form={form} />

            <Paper p="md" withBorder radius="md">
              <Stack gap="md">
                <Title order={3}>Capacity</Title>
                <Group grow>
                  <NumberInput
                    label="Min"
                    min={1}
                    max={5000}
                    {...form.getInputProps('capacity_min')}
                  />
                  <NumberInput
                    label="Max"
                    min={1}
                    max={5000}
                    {...form.getInputProps('capacity_max')}
                  />
                </Group>
              </Stack>
            </Paper>

            <Paper p="md" withBorder radius="md">
              <Stack gap="md">
                <Title order={3}>Address</Title>
                <AddressLookup
                  initialQuery={[form.values.address_line, form.values.city]
                    .filter((s): s is string => typeof s === 'string' && s.length > 0)
                    .join(', ')}
                  description="Find the address again to refresh coordinates, or edit fields below directly."
                  onSelect={(m) => {
                    track('host.address_lookup_used', { selected: true });
                    form.setValues({
                      address_line: m.addressLine,
                      city: m.city,
                      region: m.region,
                      postcode: m.postcode,
                      country: m.country,
                      lat: m.lat,
                      lng: m.lng,
                    });
                  }}
                />
                <TextInput label="Address line" {...form.getInputProps('address_line')} />
                <Group grow>
                  <TextInput label="City" {...form.getInputProps('city')} />
                  <TextInput label="Region/state" {...form.getInputProps('region')} />
                  <TextInput label="Postcode" {...form.getInputProps('postcode')} />
                </Group>
                <Group grow>
                  <Select
                    label="Country"
                    data={COUNTRIES.filter((c) => c.enabled).map((c) => ({
                      value: c.code,
                      label: c.name,
                    }))}
                    {...form.getInputProps('country')}
                  />
                  <TextInput label="Time zone" {...form.getInputProps('timezone')} />
                </Group>
                <Group grow>
                  <NumberInput
                    label="Latitude"
                    min={-90}
                    max={90}
                    decimalScale={6}
                    {...form.getInputProps('lat')}
                  />
                  <NumberInput
                    label="Longitude"
                    min={-180}
                    max={180}
                    decimalScale={6}
                    {...form.getInputProps('lng')}
                  />
                </Group>
                <Switch
                  label="Show full street address publicly"
                  {...form.getInputProps('address_visible_to_public', { type: 'checkbox' })}
                />
              </Stack>
            </Paper>

            <VenueAmenitiesFields form={form} />

            <VenueStipendFields form={form} />

            <VenueExtrasFields form={form} />

            <Group justify="flex-end">
              <Button
                type="button"
                variant="subtle"
                onClick={() => navigate({ to: '/host/venues' })}
              >
                Back
              </Button>
              <Button type="submit" loading={saving} disabled={!form.isDirty()}>
                Save changes
              </Button>
            </Group>
          </Stack>
        </form>

        <PhotoPanel
          venueId={id}
          photos={detail.photos}
          onChange={() => qc.invalidateQueries({ queryKey: ['venue', id] })}
        />

        <CalendarPanel
          venueId={id}
          entries={detail.calendar_entries}
          onChange={() => qc.invalidateQueries({ queryKey: ['venue', id] })}
        />
      </Stack>
    </AppShell>
  );
}

// ---------------------------------------------------------------------------
// Photo panel
// ---------------------------------------------------------------------------

function PhotoPanel({
  venueId,
  photos,
  onChange,
}: {
  venueId: string;
  photos: Photo[];
  onChange: () => void;
}) {
  const [altText, setAltText] = useState('');
  const [makeHero, setMakeHero] = useState(false);
  const resetRef = useRef<() => void>(null);

  const uploadM = useMutation({
    mutationFn: (file: File) => uploadPhoto(venueId, file, altText, makeHero),
    onSuccess: () => {
      setAltText('');
      setMakeHero(false);
      resetRef.current?.();
      showToast({ message: 'Saved.', variant: 'success' });
      onChange();
    },
    onError: (err: Error) => showToast({ message: err.message, variant: 'error' }),
  });
  const deleteM = useMutation({
    mutationFn: (photoId: string) => deletePhoto(venueId, photoId),
    onSuccess: () => {
      showToast({ message: 'Photo removed.', variant: 'success' });
      onChange();
    },
    onError: (err: Error) => showToast({ message: err.message, variant: 'error' }),
  });
  const reorderM = useMutation({
    mutationFn: (ordered: string[]) => reorderPhotos(venueId, ordered),
    onSuccess: () => {
      showToast({ message: 'Reordered.', variant: 'success' });
      onChange();
    },
    onError: (err: Error) => showToast({ message: err.message, variant: 'error' }),
  });

  const sortedPhotos = [...photos].sort((a, b) => a.position - b.position);

  function move(idx: number, dir: -1 | 1) {
    const next = [...sortedPhotos];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    const a = next[idx];
    const b = next[target];
    if (!a || !b) return;
    next[idx] = b;
    next[target] = a;
    reorderM.mutate(next.map((p) => p.id));
  }

  return (
    <Paper p="md" withBorder radius="md">
      <Stack gap="md">
        <Title order={3}>Photos</Title>
        <Text c="dimmed" size="sm">
          One good photo of the room beats five blurry ones. The first photo is the hero.
        </Text>

        {sortedPhotos.length === 0 ? (
          <Text c="dimmed">No photos yet.</Text>
        ) : (
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="sm">
            {sortedPhotos.map((p, idx) => (
              <Card key={p.id} withBorder padding="xs" radius="md">
                <Card.Section>
                  <Image src={p.url} alt={p.alt_text} h={160} fit="cover" />
                </Card.Section>
                <Stack gap={4} mt="xs">
                  <Group gap={4}>
                    {p.is_hero && (
                      <Badge color="brand" size="sm">
                        Hero
                      </Badge>
                    )}
                    <Text size="xs" c="dimmed" lineClamp={2}>
                      {p.alt_text}
                    </Text>
                  </Group>
                  <Group gap={4} justify="space-between">
                    <Group gap={4}>
                      <ActionIcon
                        variant="subtle"
                        size="sm"
                        onClick={() => move(idx, -1)}
                        disabled={idx === 0 || reorderM.isPending}
                        aria-label="Move up"
                      >
                        ↑
                      </ActionIcon>
                      <ActionIcon
                        variant="subtle"
                        size="sm"
                        onClick={() => move(idx, +1)}
                        disabled={idx === sortedPhotos.length - 1 || reorderM.isPending}
                        aria-label="Move down"
                      >
                        ↓
                      </ActionIcon>
                    </Group>
                    <Button
                      size="xs"
                      variant="subtle"
                      color="red"
                      onClick={() => deleteM.mutate(p.id)}
                      loading={deleteM.isPending}
                    >
                      Remove
                    </Button>
                  </Group>
                </Stack>
              </Card>
            ))}
          </SimpleGrid>
        )}

        <Stack gap="sm">
          <TextInput
            label="Alt text"
            description="Describe the photo for people using screen readers."
            value={altText}
            onChange={(e) => setAltText(e.currentTarget.value)}
            required
          />
          <Switch
            label="Make this the hero photo"
            checked={makeHero}
            onChange={(e) => setMakeHero(e.currentTarget.checked)}
          />
          <FileButton
            resetRef={resetRef}
            accept="image/png,image/jpeg,image/webp"
            onChange={(file) => {
              if (!file) return;
              if (altText.trim().length === 0) {
                showToast({
                  message: 'Add alt text first. It helps people who read with screen readers.',
                  variant: 'warning',
                });
                return;
              }
              uploadM.mutate(file);
            }}
          >
            {(props) => (
              <Button {...props} loading={uploadM.isPending} disabled={photos.length >= 12}>
                {photos.length >= 12 ? 'At the 12-photo limit' : 'Upload a photo'}
              </Button>
            )}
          </FileButton>
        </Stack>
      </Stack>
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// Calendar panel — minimal viable: list of (kind, start, end) entries.
// Replace-on-save semantics; the parent agent's wave 2c (browse) reads the
// resulting blocks/open-windows for availability filtering.
// ---------------------------------------------------------------------------

function CalendarPanel({
  venueId,
  entries,
  onChange,
}: {
  venueId: string;
  entries: CalendarEntry[];
  onChange: () => void;
}) {
  const [working, setWorking] = useState<CalendarEntry[]>(entries);
  useEffect(() => {
    setWorking(entries);
  }, [entries]);

  const saveM = useMutation({
    mutationFn: () => saveCalendar(venueId, working),
    onSuccess: () => {
      showToast({ message: 'Saved.', variant: 'success' });
      onChange();
    },
    onError: (err: Error) => showToast({ message: err.message, variant: 'error' }),
  });

  function addEntry() {
    const today = new Date().toISOString().slice(0, 10);
    const tempId = `tmp-${working.length + 1}`;
    setWorking([
      ...working,
      {
        id: tempId,
        entry_kind: 'block',
        start_date: today,
        end_date: today,
        recurrence_rule: null,
        notes: null,
      },
    ]);
  }

  function update(idx: number, patch: Partial<CalendarEntry>) {
    setWorking((prev) => {
      const next = [...prev];
      const cur = next[idx];
      if (!cur) return prev;
      next[idx] = { ...cur, ...patch };
      return next;
    });
  }

  function remove(idx: number) {
    setWorking((prev) => prev.filter((_, i) => i !== idx));
  }

  return (
    <Paper p="md" withBorder radius="md">
      <Stack gap="md">
        <Title order={3}>Availability calendar</Title>
        <Text c="dimmed" size="sm">
          Block out dates you can't host. Saving replaces the whole list.
        </Text>

        {working.length === 0 ? (
          <Text c="dimmed">No blocks yet. Your venue is open by default.</Text>
        ) : (
          <Stack gap="sm">
            {working.map((e, idx) => (
              <Card key={e.id} withBorder padding="sm" radius="sm">
                <Group align="flex-end" wrap="wrap">
                  <Select
                    label="Kind"
                    w={140}
                    data={[
                      { value: 'block', label: 'Block' },
                      { value: 'open_window', label: 'Open window' },
                    ]}
                    value={e.entry_kind}
                    onChange={(v) =>
                      v && update(idx, { entry_kind: v as 'block' | 'open_window' })
                    }
                  />
                  <TextInput
                    label="Start"
                    placeholder="YYYY-MM-DD"
                    value={e.start_date}
                    onChange={(ev) => update(idx, { start_date: ev.currentTarget.value })}
                  />
                  <TextInput
                    label="End"
                    placeholder="YYYY-MM-DD"
                    value={e.end_date}
                    onChange={(ev) => update(idx, { end_date: ev.currentTarget.value })}
                  />
                  <TextInput
                    label="Notes"
                    value={e.notes ?? ''}
                    onChange={(ev) =>
                      update(idx, {
                        notes: ev.currentTarget.value.length > 0 ? ev.currentTarget.value : null,
                      })
                    }
                    flex={1}
                  />
                  <Button variant="subtle" color="red" onClick={() => remove(idx)}>
                    Remove
                  </Button>
                </Group>
              </Card>
            ))}
          </Stack>
        )}

        <Group>
          <Button variant="light" onClick={addEntry}>
            Add entry
          </Button>
          <Button onClick={() => saveM.mutate()} loading={saveM.isPending}>
            Save calendar
          </Button>
        </Group>
      </Stack>
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// AUD-013: the status-transition toast used to say "Status updated." for
// every target — a host submitting for review got the same flat line as one
// pausing their venue. Naming the actual outcome (and the 72-hour window on
// submit) closes the loop the same way the pending_review Alert does.
function resolveTransitionToast(to: string): string {
  switch (to) {
    case 'pending_review':
      return 'Submitted for review — most decisions land within 72 hours.';
    case 'paused':
      return 'Venue paused.';
    case 'draft':
      return 'Moved back to draft.';
    default:
      return 'Status updated.';
  }
}

function toPatchPayload(v: FormValues): Record<string, unknown> {
  // Always send every multi-select field — the server treats absence as
  // "no change" and presence as "replace". This is intentional: we want the
  // saved row to reflect the form on save, including emptied selections.
  const payload: Record<string, unknown> = {
    name: v.name,
    summary: v.summary,
    description: v.description,
    venue_types: v.venue_types,
    art_forms_accepted: v.art_forms_accepted,
    vibe_tags: v.vibe_tags,
    address_line: v.address_line,
    city: v.city,
    region: v.region,
    country: v.country,
    postcode: v.postcode,
    address_visible_to_public: v.address_visible_to_public,
    timezone: v.timezone,
    sound_tech: v.sound_tech,
    sleeping_arrangements: v.sleeping_arrangements,
    food_offered: v.food_offered,
    drink_offered: v.drink_offered,
    facilities: v.facilities,
    house_rules: v.house_rules,
    stipend_offered: v.stipend_offered,
    stipend_description: v.stipend_description,
    travel_contribution_default: v.travel_contribution_default,
    travel_contribution_description: v.travel_contribution_description,
    equipment_default: v.equipment_default,
    equipment_description: v.equipment_description,
    promotion_default: v.promotion_default,
    promotion_description: v.promotion_description,
    recording_default: v.recording_default,
    recording_description: v.recording_description,
  };
  if (typeof v.capacity_min === 'number') payload['capacity_min'] = v.capacity_min;
  if (typeof v.capacity_max === 'number') payload['capacity_max'] = v.capacity_max;
  if (typeof v.lat === 'number') payload['lat'] = v.lat;
  if (typeof v.lng === 'number') payload['lng'] = v.lng;
  if (v.usual_event_times.length > 0) payload['usual_event_times'] = v.usual_event_times;
  return payload;
}

export const Route = createFileRoute('/host/venues/$id/edit')({
  component: VenueEdit,
});
